import { aabb, type Aabb } from '../physics/Aabb';

/**
 * Engine-free simulation of one moving hazard: a deterministic timeline that runs in fixed
 * steps (like the player) and exposes its pose (for the presenter) and its colliders (for the
 * collision system) after every step. No Babylon here – hazards replay on a server like the rest
 * of the gameplay.
 */
export const HazardPhase = {
  /** Waiting / idle animation – visible, but harmless or static. */
  Idle: 'IDLE',
  /** Telegraph: horn and beacon – the hazard is about to act. */
  Warning: 'WARNING',
  /** Moving into (or holding) its dangerous configuration. */
  Active: 'ACTIVE',
} as const;
export type HazardPhase = (typeof HazardPhase)[keyof typeof HazardPhase];

/** One-shot moments for audio/effects (emitted as `hazard:cue`). */
export const HazardCue = {
  /** Telegraph starts (warning beeps, horn, toast). */
  Warning: 'WARNING',
  /** A vehicle is about to change lanes. */
  Signal: 'SIGNAL',
  /** Something heavy landed (block touched down, rubble dumped). */
  Impact: 'IMPACT',
  /** A swinging load turns at the end of its swing – the chain creaks (only before the runner has passed). */
  Creak: 'CREAK',
} as const;
export type HazardCue = (typeof HazardCue)[keyof typeof HazardCue];

export interface HazardStepContext {
  /** The hazard's dynamic event socket is armed – it may start its timeline. */
  readonly armed: boolean;
  /** Seconds until the runner reaches a segment-local z at the current run speed (negative once past). */
  eta(localZ: number): number;
}

/** Collider slot in segment-local space (x lateral, y height, z along the segment). */
export interface HazardCollider {
  readonly box: Aabb;
  enabled: boolean;
}

/** Numeric pose values only – the presenter interpolates them between fixed steps. */
export type HazardPose = Record<string, number>;

export interface HazardModel<P extends HazardPose = HazardPose> {
  readonly phase: HazardPhase;
  /** Pose after the last step and the step before (render interpolation). */
  readonly pose: P;
  readonly previous: P;
  /** Fixed number of slots – the collision system compares each slot with its previous box. */
  readonly colliders: readonly HazardCollider[];
  /** Advances the timeline; pushes one-shot cues of this step. */
  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void;
}

export function colliderSlot(): HazardCollider {
  return { box: aabb(), enabled: false };
}

/** Sets a collider slot from its extents (segment-local). */
export function setBox(slot: HazardCollider, minX: number, maxX: number, minY: number, maxY: number, minZ: number, maxZ: number): void {
  const b = slot.box;
  b.minX = minX;
  b.maxX = maxX;
  b.minY = minY;
  b.maxY = maxY;
  b.minZ = minZ;
  b.maxZ = maxZ;
  slot.enabled = true;
}

/** Interpolates two poses of the same shape (render frames between fixed steps). */
export function lerpPose<P extends HazardPose>(from: P, to: P, t: number, out: P): P {
  const a: HazardPose = from;
  const b: HazardPose = to;
  const o: HazardPose = out;
  for (const key in b) {
    const start = a[key] ?? 0;
    o[key] = start + ((b[key] ?? 0) - start) * t;
  }
  return out;
}
